import React from 'react';
import Icon from '../../../components/AppIcon';

const ProductChoiceSection = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el?.scrollIntoView({ behavior: 'smooth' });
  };

  const grapheneProduct = {
    id: 'graphene',
    badge: 'Nieuwste technologie',
    badgeColor: 'bg-primary text-primary-foreground',
    name: 'GRP Graphene',
    tagline: 'Maximale levensduur & nieuwste technologie',
    image: '/assets/images/GRP-Wall-1783593982170.png',
    imageAlt: 'GRP Wall thuisbatterij — wandgemonteerd grafeen model voor thuis',
    bullets: [
      'Ontworpen voor 20.000 laadcycli — meer dan 3× langer dan standaard lithium',
      'Ontlaadt tot −20°C — optimale prestaties ook in de winter',
      'Superieur veiligheidsprofiel — UN 38.3 getest door Kiwa Primara',
      'Stille passieve koeling — geen ventilator, geen geluid',
      '15 jaar garantie (stapelbaar model)',
    ],
    price: 'Vanaf €5.000',
    priceNote: 'inclusief installatie',
    leasePrice: 'Vanaf €49 / maand',
    leasePriceNote: 'operational lease, all-in',
  };

  const marketProviders = [
    {
      name: 'Tesla Powerwall 3',
      capacity: '13,5 kWh',
      cycles: '~4.000 cycli',
      warranty: '10 jaar',
      country: 'VS',
    },
    {
      name: 'AlphaESS SMILE5',
      capacity: '5,7–22,8 kWh',
      cycles: '6.000 cycli',
      warranty: '10 jaar',
      country: 'CN',
    },
    {
      name: 'Sessy Battery',
      capacity: '5 kWh',
      cycles: '~4.000 cycli',
      warranty: '10 jaar',
      country: 'NL',
    },
    {
      name: 'Sigenergy SigenStor',
      capacity: '5–30 kWh',
      cycles: '6.000 cycli',
      warranty: '10 jaar',
      country: 'CN',
    },
    {
      name: 'Zonneplan Nexus',
      capacity: '5–15 kWh',
      cycles: '~4.000 cycli',
      warranty: '10 jaar',
      country: 'NL',
    },
    {
      name: 'HomeWizard Energy',
      capacity: '5 kWh',
      cycles: '~4.000 cycli',
      warranty: '5 jaar',
      country: 'NL',
    },
  ];

  return (
    <section id="productkeuze" className="py-16 lg:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <div className="inline-flex items-center space-x-2 text-primary bg-primary/10 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider mb-4">
            <Icon name="Battery" size={12} />
            <span>GRP Grafeenbatterij</span>
          </div>
          <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground leading-tight">
            De GRP Graphene — gebouwd voor de lange termijn
          </h2>
        </div>

        {/* Single product card — full width */}
        <div className="max-w-2xl mx-auto mb-14">
          <div className="relative rounded-2xl border border-primary shadow-elevation-2 ring-2 ring-primary/20 overflow-hidden">
            {/* Badge */}
            <div className="absolute top-4 left-4 z-10">
              <span className={`text-xs font-heading font-semibold px-3 py-1 rounded-full ${grapheneProduct?.badgeColor}`}>
                {grapheneProduct?.badge}
              </span>
            </div>

            {/* Image */}
            <div className="relative h-56 overflow-hidden">
              <img
                src={grapheneProduct?.image}
                alt={grapheneProduct?.imageAlt}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <h3 className="text-xl font-heading font-bold text-foreground">{grapheneProduct?.name}</h3>
                <p className="text-sm font-body text-muted-foreground mt-1">{grapheneProduct?.tagline}</p>
              </div>

              <ul className="space-y-2">
                {grapheneProduct?.bullets?.map((bullet, i) => (
                  <li key={i} className="flex items-start space-x-2">
                    <div className="w-5 h-5 rounded-full bg-primary/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon name="Check" size={11} className="text-primary" />
                    </div>
                    <span className="text-sm font-body text-foreground">{bullet}</span>
                  </li>
                ))}
              </ul>

              {/* Price */}
              <div className="pt-2 border-t border-border flex items-stretch gap-3">
                <div className="flex-1">
                  <p className="text-2xl font-heading font-bold text-foreground">{grapheneProduct?.price}</p>
                  <p className="text-xs font-caption text-muted-foreground">{grapheneProduct?.priceNote}</p>
                </div>
                <div className="flex items-center space-x-2 bg-primary/5 rounded-lg px-3 py-2 flex-1">
                  <Icon name="CreditCard" size={14} className="text-primary flex-shrink-0" />
                  <div>
                    <p className="text-base font-heading font-semibold text-primary">{grapheneProduct?.leasePrice}</p>
                    <p className="text-xs font-caption text-muted-foreground">{grapheneProduct?.leasePriceNote}</p>
                  </div>
                </div>
              </div>

              <button
                onClick={() => scrollTo('formulier')}
                className="w-full py-3 font-heading font-semibold text-sm rounded-md transition-colors bg-primary text-primary-foreground hover:bg-primary/90"
              >
                Adviesgesprek plannen
              </button>
            </div>
          </div>
        </div>

        {/* Market providers overview */}
        <div className="mb-10">
          <div className="text-center max-w-2xl mx-auto mb-8">
            <div className="inline-flex items-center space-x-2 text-muted-foreground bg-slate-100 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider mb-4">
              <Icon name="ShoppingBag" size={12} />
              <span>Gangbare marktaanbieders</span>
            </div>
            <h3 className="text-2xl font-heading font-bold text-foreground leading-tight">
              Meest aangeboden batterijen in Nederland &amp; België
            </h3>
            <p className="mt-3 font-body text-muted-foreground text-sm">
              Ter vergelijking: de meest voorkomende thuisbatterijen op de Nederlandse en Belgische markt.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
            {/* Table header */}
            <div className="grid grid-cols-[2fr_1fr_1fr_1fr_0.5fr] border-b border-border bg-slate-50">
              <div className="px-4 py-3">
                <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Aanbieder / Model</span>
              </div>
              <div className="px-4 py-3 border-l border-border">
                <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Capaciteit</span>
              </div>
              <div className="px-4 py-3 border-l border-border">
                <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Laadcycli</span>
              </div>
              <div className="px-4 py-3 border-l border-border">
                <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Garantie</span>
              </div>
              <div className="px-4 py-3 border-l border-border">
                <span className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Land</span>
              </div>
            </div>

            {/* Rows */}
            {marketProviders?.map((provider, i) => (
              <div
                key={provider?.name}
                className={`grid grid-cols-[2fr_1fr_1fr_1fr_0.5fr] border-b border-border/50 last:border-0 ${i % 2 === 0 ? '' : 'bg-slate-50/40'}`}
              >
                <div className="px-4 py-3.5 flex items-center">
                  <span className="text-sm font-body font-semibold text-foreground">{provider?.name}</span>
                </div>
                <div className="px-4 py-3.5 border-l border-border/50 flex items-center">
                  <span className="text-sm font-body text-muted-foreground">{provider?.capacity}</span>
                </div>
                <div className="px-4 py-3.5 border-l border-border/50 flex items-center">
                  <span className="text-sm font-body text-muted-foreground">{provider?.cycles}</span>
                </div>
                <div className="px-4 py-3.5 border-l border-border/50 flex items-center">
                  <span className="text-sm font-body text-muted-foreground">{provider?.warranty}</span>
                </div>
                <div className="px-4 py-3.5 border-l border-border/50 flex items-center">
                  <span className="text-xs font-caption text-muted-foreground">{provider?.country}</span>
                </div>
              </div>
            ))}
          </div>

          <p className="text-xs font-caption text-muted-foreground italic mt-3 text-center">
            Marktwaarden zijn indicatief voor 2026. Specificaties kunnen per configuratie en firmware-versie afwijken.
          </p>
        </div>

        {/* Choice helper */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-start space-x-3">
          <Icon name="HelpCircle" size={18} className="text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm font-body text-foreground">
            <strong>Twijfel je?</strong> In het adviesgesprek rekenen we de beste optie voor jouw situatie door. Geen verplichtingen, geen verkooppraatje.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ProductChoiceSection;
